/**
 * WorkItemRelation 数据服务
 */

import {
  injectable,
  inject,
  observable,
  action,
  runInAction,
  postConstruct,
  ICacheService,
} from '@meego-remote/core/app/contract'
import type { CacheService } from '@meego-remote/core/app/service'
import { IWorkItemRelationService, ISettingApiService, RPC } from '#contract'
import type { WorkItemRelation } from '../types'

/**
 * 获取所有工作项关联关系
 */
@injectable({ implements: [IWorkItemRelationService] })
export class WorkItemRelationService implements IWorkItemRelationService {
  @inject(ISettingApiService)
  private settingApiService!: ISettingApiService

  @inject(ICacheService)
  private cacheService!: CacheService

  // 直接在 Service 上声明 @observable 属性
  @observable
  public relationList: WorkItemRelation[] = []

  @observable
  public isFetchingRelationList: boolean = false

  // 记录当前正在进行的请求，防止并发重复请求
  private fetching: Map<string, Promise<WorkItemRelation[]>> = new Map()

  private get relationCache() {
    return this.cacheService?.getCacheGroup<WorkItemRelation[]>('WorkItemRelationService')
  }

  @postConstruct()
  protected init() {
    // 与原仓库 getAllWobjRelationList 的 staleTime 保持一致 (5分钟)
    this.relationCache?.setOptions({ defaultMaxAge: 5 * 60 * 1000 })
  }

  @action('fetchRelationList')
  public async getAll(projectKey: string): Promise<WorkItemRelation[]> {
    if (!projectKey) {
      runInAction(() => {
        this.relationList = []
      })
      return []
    }

    // 在 await 之前设置 - action 上下文内，直接赋值，不需要 runInAction
    this.isFetchingRelationList = true

    // 使用缓存
    const cached = this.relationCache?.get(projectKey)
    if (cached) {
      // await 之后的代码不在 @action 上下文中，需要使用 runInAction
      runInAction(() => {
        this.relationList = cached
        this.isFetchingRelationList = false
      })
      return cached
    }

    // 检查是否有正在进行的请求（防重）
    const existingRequest = this.fetching.get(projectKey)
    if (existingRequest) {
      return existingRequest
    }

    // 创建新请求
    const request = this.settingApiService
      // @ts-expect-error operator 参数前端非必填
      .request(RPC.APIFetchAllWorkItemRelationByProjectKey, { project_key: projectKey })

    this.fetching.set(projectKey, request as Promise<WorkItemRelation[]>)

    try {
      const data = await request as WorkItemRelation[]
      // 更新缓存
      this.relationCache?.set(projectKey, data)

      // await 之后的代码不在 @action 上下文中，需要使用 runInAction
      runInAction(() => {
        this.relationList = data
      })
      return data
    }
    catch {
      // await 之后的代码不在 @action 上下文中，需要使用 runInAction
      runInAction(() => {
        this.relationList = []
      })
      return []
    }
    finally {
      // await 之后的代码不在 @action 上下文中，需要使用 runInAction
      runInAction(() => {
        this.isFetchingRelationList = false
      })
      this.fetching.delete(projectKey)
    }
  }
}
